import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGeri } from '@/lib/geri';
import api from '@/lib/api';
import NewTransactionModal from '@/components/NewTransactionModal';
import { dekontuAc } from '@/lib/dekont';
import EkDugmesi from '@/components/HareketEkleri';
import { ekstreIndir } from '@/lib/ekstre';
import { Receipt } from 'lucide-react';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, Plus, Wallet, Building2, CreditCard, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Loader2, Search, SlidersHorizontal, X, FileSpreadsheet, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { trNorm } from '@/lib/arama';

const fmtTR = (n, d = 2) =>
  n == null ? '—' : Number(n).toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d });

// Hesaplar `suppliers` koleksiyonunda tutulur; kasa/banka hesaplarının
// account_type değeri "Kasa ve Bankalar"dır. Görsel ayrım hesap ADINDAN
// çıkarılır (banka / POS / kredi kartı / kasa).
const accountVisualKind = (account) => {
  const name = (account?.name || '').toLowerCase();
  if (name.includes('pos')) return 'pos';
  if (name.includes('kredi kart')) return 'card';
  if (name.includes('bank') || name.includes('banka')) return 'bank';
  return 'cash';
};
const accountKindLabels = { cash: 'Kasa', bank: 'Banka', pos: 'POS', card: 'Kredi Kartı' };
const accountKindColors = {
  cash: 'bg-[#7a6a2e]/10 text-[#7a6a2e]',
  bank: 'bg-[#2e6fa0]/10 text-[#2e6fa0]',
  pos: 'bg-[#5a4e8a]/10 text-[#5a4e8a]',
  card: 'bg-[#5a4e8a]/10 text-[#5a4e8a]',
};
const AccountIcon = ({ type, size = 18 }) => {
  if (type === 'bank') return <Building2 size={size} className="text-[#2e6fa0]" strokeWidth={1.8} />;
  if (type === 'pos' || type === 'card') return <CreditCard size={size} className="text-[#5a4e8a]" strokeWidth={1.8} />;
  return <Wallet size={size} className="text-[#7a6a2e]" strokeWidth={1.8} />;
};

// ── Ana sayfa ────────────────────────────────────────────────────────────────
export default function AccountDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Geri = önceki sayfa; önceki adım yoksa eski liste adresi (bkz. lib/geri.js)
  const geri = useGeri('/erp/treasury');

  const [account, setAccount] = useState(null);
  usePageTitle(account?.name || 'Hesap Detayı');
  const [accounts, setAccounts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // Filtreler
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [currFilter, setCurrFilter] = useState('');

  // Modal
  const [showModal, setShowModal] = useState(false);

  const PER_PAGE = 30;

  const fetchAll = useCallback(() => {
    api.get(`/treasury/accounts`).then(r => {
      setAccounts(r.data);
      const found = r.data.find(a => a.id === id);
      setAccount(found || null);
    }).catch(() => {});
    api.get('/suppliers').then(r => {
      const u = []; const s = new Set();
      r.data.forEach(x => { if (!s.has(x.id)) { s.add(x.id); u.push(x); } });
      setSuppliers(u);
    }).catch(() => {});
  }, [id]);

  // `sessiz`: ek yükleme/silme sonrası yenilemede tablo "yükleniyor"a DÜŞMEZ —
  // düşerse satırlar sökülür ve ekler penceresi işlem biter bitmez kapanırdı.
  const fetchTxns = useCallback((sessiz) => {
    if (sessiz !== true) setLoading(true);
    const params = { account_id: id, page: 1, per_page: 9999 };
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo)   params.date_to   = dateTo;
    api.get('/treasury/transactions', { params })
      .then(r => setAllTransactions(r.data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id, dateFrom, dateTo]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { fetchTxns(); }, [fetchTxns]);
  useEffect(() => { setPage(1); }, [dateFrom, dateTo, search, currFilter]);

  // Per-currency balance summary
  const balances = {};
  for (const t of allTransactions) {
    const c = t.currency || 'TRY';
    if (!balances[c]) balances[c] = { in: 0, out: 0 };
    if (t.debit_account_id === id)       balances[c].in  += parseFloat(t.amount || 0);
    else if (t.credit_account_id === id) balances[c].out += parseFloat(t.amount || 0);
  }
  const currencyKeys = Object.keys(balances).sort();

  const filteredAll = allTransactions.filter(t => {
    if (currFilter && t.currency !== currFilter) return false;
    if (search.trim()) {
      const q = trNorm(search);
      if (!trNorm(t.description).includes(q)
        && !trNorm(t.debit_account_name).includes(q)
        && !trNorm(t.credit_account_name).includes(q)) return false;
    }
    return true;
  });

  // İşlem tarihine göre sırala (aynı tarihte oluşturulma sırası ikincil)
  const sortedAsc = [...filteredAll].sort((a, b) => {
    const d = (a.transaction_date || '').localeCompare(b.transaction_date || '');
    return d !== 0 ? d : (a.created_at || '').localeCompare(b.created_at || '');
  });

  // Running balance: eski→yeni kümülatif hesapla, sonra yeni→eski göster
  const ledgerWithBalance = (() => {
    const running = {};
    const withBalance = sortedAsc.map(t => {
      const c = t.currency || 'TRY';
      if (running[c] === undefined) running[c] = 0;
      if (t.debit_account_id === id)       running[c] += parseFloat(t.amount || 0);
      else if (t.credit_account_id === id) running[c] -= parseFloat(t.amount || 0);
      return { ...t, runningBalance: running[c] };
    });
    return withBalance.reverse();
  })();

  // Döküm EKRANDAKİ süzgeçle sınırlı (sayfalama hariç: görünen sayfa değil,
  // süzgeçten geçen TÜM satırlar yazılır — ekstre parça parça anlamsız olurdu).
  const ekstreAl = (bicim) => ekstreIndir({
    yol: `/treasury/accounts/${id}/ekstre`,
    bicim,
    satirIdler: ledgerWithBalance.map(t => t.id),
    filtre: [
      (dateFrom || dateTo) ? `Tarih: ${dateFrom || '…'} – ${dateTo || '…'}` : 'Tarih: Tümü',
      `Para birimi: ${currFilter || 'Tümü'}`,
      search ? `Arama: ${search}` : null,
    ].filter(Boolean).join(' · '),
    ad: account?.name,
  });

  const totalPages = Math.ceil(ledgerWithBalance.length / PER_PAGE);
  const hasActiveFilter = dateFrom || dateTo || search || currFilter;
  const pageItems = ledgerWithBalance.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  if (!account && accounts.length > 0) return (
    <div className="text-center py-12 text-[#8a7e70]">Hesap bulunamadı</div>
  );
  if (!account) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 size={24} className="animate-spin text-[#8a7e70]" />
    </div>
  );

  return (
    <div data-testid="account-detail-page">
      {/* Modal */}
      <NewTransactionModal
        open={showModal}
        onClose={() => setShowModal(false)}
        accounts={accounts}
        suppliers={suppliers}
        defaultAccountId={id}
        defaultCurrency={account?.currency || 'TRY'}
        onSaved={() => { fetchAll(); fetchTxns(); }}
      />

      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <Button variant="ghost" onClick={geri} className="text-[#8a7e70] hover:text-[#1a1916]">
          <ArrowLeft size={18} />
        </Button>
        <div className="flex-1 flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${accountKindColors[accountVisualKind(account)] || 'bg-[#e8dfd0]'}`}>
            <AccountIcon type={accountVisualKind(account)} size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-[#1a1916] tracking-tight">{account.name}</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${accountKindColors[accountVisualKind(account)] || 'bg-[#e8dfd0] text-[#3F3E39]'}`}>
                {accountKindLabels[accountVisualKind(account)]}
              </span>
              {account.bank_name && <span className="text-xs text-[#8a7e70]">{account.bank_name}</span>}
            </div>
          </div>
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" onClick={() => ekstreAl('xlsx')}
              className="border-[#d0c4b0] text-[#3d6e4e] hover:bg-[#3d6e4e]/10 h-8 text-xs px-3"
              title="Ekrandaki ekstreyi Excel olarak indir" data-testid="ekstre-excel">
              <FileSpreadsheet size={13} className="mr-1" /> Excel
            </Button>
            <Button size="sm" variant="outline" onClick={() => ekstreAl('pdf')}
              className="border-[#d0c4b0] text-[#7a3535] hover:bg-[#7a3535]/10 h-8 text-xs px-3"
              title="Ekrandaki ekstreyi PDF olarak indir" data-testid="ekstre-pdf">
              <FileDown size={13} className="mr-1" /> PDF
            </Button>
            <Button size="sm" onClick={() => setShowModal(true)}
              className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] h-8 text-xs px-3">
              <Plus size={13} className="mr-1" /> Yeni Hareket
            </Button>
          </div>
        </div>
      </div>

      {/* Bakiye kartları */}
      {currencyKeys.length > 0 && (
        <div className={`grid gap-4 mb-5 ${currencyKeys.length === 1 ? 'grid-cols-1 max-w-xs' : currencyKeys.length === 2 ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4'}`}>
          {currencyKeys.map(curr => {
            const b = balances[curr];
            const net = b.in - b.out;
            return (
              <Card key={curr} className="bg-[#faf7f2] border-[#d0c4b0]">
                <CardContent className="p-4 text-center">
                  <p className="text-xs font-bold uppercase text-[#8a7e70] mb-2">{curr} Bakiye</p>
                  <p className={`text-xl font-bold ${net >= 0 ? 'text-[#3d6e4e]' : 'text-[#7a3535]'}`}>
                    {fmtTR(net)}
                  </p>
                  <p className="text-xs text-[#8a7e70] mt-1">
                    Giren: {fmtTR(b.in)} | Çıkan: {fmtTR(b.out)}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Hareketler Tablosu */}
      <Card className="bg-[#faf7f2] border-[#d0c4b0]">
        {/* Üst bar */}
        <div className="px-3 py-2 border-b border-[#e8dfd0] flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[160px]">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8a7e70]" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Açıklama veya hesap ara..."
              className="pl-7 h-7 text-[10px] bg-white border-[#d0c4b0]" />
          </div>
          {currencyKeys.length > 1 && (
            <select value={currFilter} onChange={e => setCurrFilter(e.target.value)}
              className="h-7 px-2 rounded-md border border-[#d0c4b0] bg-white text-[10px] focus:outline-none">
              <option value="">Tüm Kurlar</option>
              {currencyKeys.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          <Button variant="outline" size="sm"
            onClick={() => setShowFilters(v => !v)}
            className={`h-7 text-[10px] border-[#d0c4b0] px-2 ${(dateFrom || dateTo) ? 'border-[#3d6e4e] text-[#3d6e4e]' : 'text-[#8a7e70]'}`}>
            <SlidersHorizontal size={11} className="mr-1" />
            Tarih {(dateFrom || dateTo) ? '●' : ''}
          </Button>
          <div className="flex items-center gap-1.5 text-[10px] text-[#8a7e70]">
            {hasActiveFilter
              ? <span>{ledgerWithBalance.length}/{allTransactions.length} kayıt</span>
              : <span>{allTransactions.length} kayıt</span>}
          </div>
        </div>

        {/* Filtre satırı */}
        {showFilters && (
          <div className="px-3 py-2 border-b border-[#e8dfd0] flex items-center gap-3 flex-wrap bg-[#f5f0e8]/50">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-[#8a7e70] whitespace-nowrap">Başlangıç:</span>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                className="h-7 text-[10px] bg-white border-[#d0c4b0] w-32" />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-[#8a7e70] whitespace-nowrap">Bitiş:</span>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                className="h-7 text-[10px] bg-white border-[#d0c4b0] w-32" />
            </div>
            {(dateFrom || dateTo) && (
              <button onClick={() => { setDateFrom(''); setDateTo(''); }}
                className="text-[10px] text-[#7a3535] hover:underline">Temizle</button>
            )}
          </div>
        )}

        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                  <TableHead className="text-[#1a1916] font-bold text-[9px] uppercase py-1 w-24">Tarih</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-[9px] uppercase py-1">Açıklama</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-[9px] uppercase py-1">Karşı Hesap</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-[9px] uppercase py-1 w-12 text-center">PB</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-[9px] uppercase py-1 text-right w-32">Tutar</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-[9px] uppercase py-1 text-right w-32">Bakiye</TableHead>
                  <TableHead className="w-8 py-1" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <Loader2 size={20} className="animate-spin text-[#8a7e70] mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : pageItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12">
                      <div className="flex flex-col items-center gap-2">
                        <Wallet size={32} className="text-[#d0c4b0]" />
                        <p className="text-sm text-[#8a7e70]">Henüz hareket yok</p>
                        <Button size="sm" variant="outline" onClick={() => setShowModal(true)} className="border-[#d0c4b0] mt-1">
                          <Plus size={13} className="mr-1" /> İlk hareketi ekle
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  pageItems.map(t => {
                    const isDebit  = t.debit_account_id  === id;
                    const isCredit = t.credit_account_id === id;
                    const counterparty = isDebit
                      ? (t.credit_account_name || t.counterparty_name || '—')
                      : (t.debit_account_name  || t.counterparty_name || '—');
                    const bal = t.runningBalance;
                    const balSuffix = bal > 0 ? ' A' : bal < 0 ? ' B' : '';
                    return (
                      <TableRow key={t.id} className="erp-table-row border-[#d0c4b0]">
                        <TableCell className="text-[10px] text-[#8a7e70] font-mono py-1 whitespace-nowrap">{t.transaction_date}</TableCell>
                        <TableCell className="text-[10px] text-[#1a1916] py-1 max-w-[200px] truncate" title={t.description}>
                          {t.description || <span className="text-[#8a7e70]">—</span>}
                        </TableCell>
                        <TableCell className="text-[10px] text-[#5a5650] py-1 max-w-[160px] truncate" title={counterparty}>
                          {counterparty}
                        </TableCell>
                        <TableCell className="text-center text-[9px] font-bold text-[#8a7e70] py-1 uppercase">
                          {t.currency || '—'}
                        </TableCell>
                        <TableCell className={`text-right font-mono font-semibold text-[10px] py-1 whitespace-nowrap ${isDebit ? 'text-[#3d6e4e]' : isCredit ? 'text-[#7a3535]' : 'text-[#1a1916]'}`}>
                          {isDebit ? '+' : isCredit ? '-' : ''}{fmtTR(t.amount)}
                        </TableCell>
                        <TableCell className={`text-right font-mono font-semibold text-[10px] py-1 whitespace-nowrap ${bal < 0 ? 'text-[#7a3535]' : bal > 0 ? 'text-[#3d6e4e]' : 'text-[#1a1916]'}`}>
                          {fmtTR(Math.abs(bal))}<span className="text-[9px] font-normal ml-0.5">{balSuffix}</span>
                        </TableCell>
                        <TableCell className="py-1 text-right whitespace-nowrap">
                          <button type="button" onClick={() => dekontuAc(t.id, false)}
                            title="Ödeme dekontunu görüntüle" aria-label="Ödeme dekontu"
                            className="inline-flex items-center justify-center w-6 h-6 rounded text-[#8a7e70] hover:text-[#2e6fa0] hover:bg-[#2e6fa0]/10 transition-colors"
                            data-testid={`hesap-dekont-${t.id}`}>
                            <Receipt size={12} />
                          </button>
                          <EkDugmesi hareketId={t.id} sayi={t.ek_sayisi} onDegisti={() => fetchTxns(true)} boyut="xs" />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Sayfalama */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-3 py-2 border-t border-[#e8dfd0]">
              <span className="text-[10px] text-[#8a7e70]">
                {((page - 1) * PER_PAGE) + 1}–{Math.min(page * PER_PAGE, ledgerWithBalance.length)} / {ledgerWithBalance.length} kayıt
              </span>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-7 w-7 border-[#d0c4b0]"
                  disabled={page <= 1} onClick={() => setPage(1)}><ChevronsLeft size={13} /></Button>
                <Button variant="outline" size="icon" className="h-7 w-7 border-[#d0c4b0]"
                  disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={13} /></Button>
                <span className="text-[10px] text-[#1a1916] font-semibold px-2 min-w-[64px] text-center">
                  {page} / {totalPages}
                </span>
                <Button variant="outline" size="icon" className="h-7 w-7 border-[#d0c4b0]"
                  disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight size={13} /></Button>
                <Button variant="outline" size="icon" className="h-7 w-7 border-[#d0c4b0]"
                  disabled={page >= totalPages} onClick={() => setPage(totalPages)}><ChevronsRight size={13} /></Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
